import React, { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router";
import { server_url } from "../../Config/API";
import moment from "moment/moment";
import AuthUser from "../../Hooks/authUser";
import UpdateHooks from "../../Hooks/UpdateHooks";
import PostHooks from "../../Hooks/PostHooks";
import GameStatsIndicator from "./GameStatsIndicator";
import hitSound from "../../Assets/final new sound/invalid-selection-39351.mp3";

const JumpAndRunGame = () => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const navigate = useNavigate();
  const [score, setScore] = useState(0);
  const [gameOver, setGameOver] = useState(false);
  
  const hitAudioRef = useRef(null);
  if (!hitAudioRef.current && typeof window !== "undefined") {
    hitAudioRef.current = new Audio(hitSound);
  }

  const canvasRef = useRef(null);
  const requestIdRef = useRef(null);
  const startTimeRef = useRef(null);
  const obstaclesRef = useRef([]);
  const isInvulnerableRef = useRef(false);
  const flashTimerRef = useRef(0);
  const frameCountRef = useRef(0);

  const CANVAS_WIDTH = window.innerWidth;
  const CANVAS_HEIGHT = window.innerHeight / 2;

  const playerRef = useRef({
    x: CANVAS_WIDTH / 2,
    y: CANVAS_HEIGHT - 13,
    radius: 6 * 1.7,
    speedY: 0,
    gravity: 0.4,
    jumpForce: -10,
    isJumping: false,
  });

  const ground = {
    y: CANVAS_HEIGHT - 1,
    thickness: 1,
  };

  const obstacleSpeed = 4;
  const obstacleSpawnRate = 90;

  const [randomData, setRandomData] = useState([]);
  const { userInfo } = AuthUser();
  const date = useRef(moment().format("YYYY-MM-D")).current;
  const time = useRef(moment().format("h:mm a")).current;
  
  const previousStateRef = useRef(null);
  const scoreRef = useRef(0);
  const timeRef = useRef(300);
  const [displayScore, setDisplayScore] = useState(0);
  const [displayTime, setDisplayTime] = useState("5:00");

  const fetchFunction = async () => {
    try {
      const response = await fetch(
        `${server_url}/randomObstacle/specific?fieldName=userId&fieldValue=${
          userInfo?._id
        }&fieldName2=date&fieldValue2=${date}&fieldName3=gameName&fieldValue3=Random+Obstacle+Monocular`
      );
      const data = await response.json();
      setRandomData(data?.data || []);
      if (data?.data && data?.data.length) {
        scoreRef.current = data?.data[0]?.score;
        previousStateRef.current = data?.data;
      }
    } catch (error) {
      console.error("Error fetching jump-run data:", error);
    }
  };

  useEffect(() => {
    if (randomData?.length) {
      timeRef.current = randomData[0]?.remainingTime;
      scoreRef.current = randomData[0]?.score;
    }
  }, [randomData]);

  useEffect(() => {
    fetchFunction();
  }, []);

  const insertFunction = useCallback(() => {
    if (previousStateRef?.current?.length) {
      UpdateHooks(
        `${server_url}/randomObstacle/updateRandomObstacle/${previousStateRef?.current[0]?._id}`,
        {
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        }
      );
    } else {
      PostHooks(
        `${server_url}/randomObstacle/addRandomObstacle`,
        {
          gameName: "Random Obstacle Monocular",
          userId: userInfo?._id,
          userName: userInfo?.name,
          date,
          time,
          score: scoreRef.current,
          remainingTime: timeRef.current,
        },
        "Random Obstacle score updated !"
      );
    }
  }, [userInfo, date, time]);

  const insertFunctionRef = useRef(insertFunction);
  useEffect(() => {
    insertFunctionRef.current = insertFunction;
  }, [insertFunction]);

  const toggleFullScreenRef = useRef(null);
  const toggleFullScreen = () => {
    if (!isFullScreen) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen().catch((error) => {
          console.error("Error entering full screen:", error);
        });
      }
    }
    setIsFullScreen(!isFullScreen);
  };
  toggleFullScreenRef.current = toggleFullScreen;

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (
        e.code === "Space" &&
        !playerRef.current.isJumping
      ) {
        playerRef.current.speedY = playerRef.current.jumpForce;
        playerRef.current.isJumping = true;
      }
    };

    window.addEventListener("keydown", handleKeyDown);

    startTimeRef.current = Date.now();
    requestIdRef.current = requestAnimationFrame(gameLoop);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      cancelAnimationFrame(requestIdRef.current);
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      setDisplayScore(scoreRef.current);
      const minutes = Math.floor(timeRef.current / 60);
      const seconds = timeRef.current % 60;
      setDisplayTime(`${minutes}:${seconds.toString().padStart(2, "0")}`);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
      if (timeRef.current > 0) {
        timeRef.current = timeRef.current - 1;
      } else {
        insertFunctionRef.current();
        navigate(-1);
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const checkCollision = (circle, rect) => {
    const distX = Math.abs(circle.x - (rect.x + rect.width / 2));
    const distY = Math.abs(circle.y - (rect.y + rect.height / 2));

    if (distX > rect.width / 2 + circle.radius) return false;
    if (distY > rect.height / 2 + circle.radius) return false;

    if (distX <= rect.width / 2) return true;
    if (distY <= rect.height / 2) return true;

    const dx = distX - rect.width / 2;
    const dy = distY - rect.height / 2;
    return dx * dx + dy * dy <= circle.radius * circle.radius;
  };

  const update = () => {
    if (isInvulnerableRef.current) {
      flashTimerRef.current++;
      if (flashTimerRef.current >= 60) {
        isInvulnerableRef.current = false;
        flashTimerRef.current = 0;
      }
    }

    const player = playerRef.current;
    if (player.isJumping) {
      player.speedY += player.gravity;
      player.y += player.speedY;

      if (player.y + player.radius > ground.y - ground.thickness) {
        player.y = ground.y - ground.thickness - player.radius;
        player.speedY = 0;
        player.isJumping = false;
      }
    }

    frameCountRef.current++;
    if (frameCountRef.current % obstacleSpawnRate === 0) {
      const width = 20 * 1.7;
      const height = (40 + Math.random() * 20) * 1.7;

      obstaclesRef.current.push({
        x: CANVAS_WIDTH,
        y: ground.y - height,
        width,
        height,
      });
    }

    obstaclesRef.current.forEach((obstacle, index) => {
      obstacle.x -= obstacleSpeed;

      if (obstacle.x + obstacle.width < 0) {
        obstaclesRef.current.splice(index, 1);
        scoreRef.current = scoreRef.current + 1;
      }

      if (!isInvulnerableRef.current && checkCollision(player, obstacle)) {
        if (scoreRef.current > 0) {
          scoreRef.current -= 2;

          if (hitAudioRef.current) {
            hitAudioRef.current.currentTime = 0;
            hitAudioRef.current.play().catch(e => console.log(e));
          }
        }

        isInvulnerableRef.current = true;

        setTimeout(() => {
          if (!checkCollision(player, obstacle)) {
            isInvulnerableRef.current = false;
          }
        }, 1000);
      }
    });
  };

  const draw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    ctx.clearRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Draw ground
    ctx.beginPath();
    ctx.moveTo(0, ground.y);
    ctx.lineTo(CANVAS_WIDTH, ground.y);
    ctx.strokeStyle = "black";
    ctx.lineWidth = ground.thickness;
    ctx.stroke();

    // Draw player
    const player = playerRef.current;
    ctx.beginPath();
    ctx.arc(player.x, player.y, player.radius, 0, Math.PI * 2);
    ctx.fillStyle =
      isInvulnerableRef.current && flashTimerRef.current % 10 < 5
        ? "red"
        : "black";
    ctx.fill();
    ctx.closePath();

    // Draw obstacles
    ctx.fillStyle = "black";
    obstaclesRef.current.forEach((obstacle) => {
      ctx.fillRect(obstacle.x, obstacle.y, obstacle.width, obstacle.height);
    });

    if (gameOver) {
      ctx.fillStyle = "rgba(255, 255, 255, 0.8)";
      ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

      ctx.fillStyle = "black";
      ctx.font = "24px monospace";
      ctx.textAlign = "center";
      ctx.fillText("Time's Up!", CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2);
      ctx.fillText(
        `Final Score: ${score}`,
        CANVAS_WIDTH / 2,
        CANVAS_HEIGHT / 2 + 30
      );
      ctx.font = "16px monospace";
      ctx.fillText(
        "Refresh to play again",
        CANVAS_WIDTH / 2,
        CANVAS_HEIGHT / 2 + 60
      );
    }
  };

  const gameLoop = () => {
    if (!gameOver) {
      update();
      draw();
      requestIdRef.current = requestAnimationFrame(gameLoop);
    }
  };

  useEffect(() => {
    toggleFullScreen();
  }, []);

  useEffect(() => {
    const handleKeyPress = async (event) => {
      if (event.keyCode === 27 && isFullScreen) {
        insertFunctionRef.current();
        await navigate("/userDashboard/overview");
        if (toggleFullScreenRef.current) toggleFullScreenRef.current();
      }
    };

    document.addEventListener("keydown", handleKeyPress);
    return () => {
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [isFullScreen, navigate]);

  return (
    <div tabIndex="0" className="w-full h-screen inset-0 fixed bg-[#f7f7f7]">
      <GameStatsIndicator score={displayScore} timeLeft={timeRef.current} />
      <canvas
        ref={canvasRef}
        width={CANVAS_WIDTH}
        height={CANVAS_HEIGHT}
        className=" bg-[#f7f7f7]"
      />
    </div>
  );
};

export default JumpAndRunGame;
